/**
 * Ricevuta input schemas — F-008 Phase 2 (IMPL-P2-007).
 *
 * Schemi Zod condivisi tra API (validazione body request) e frontend
 * (validazione react-hook-form) per il flusso operatore di upload ricevuta:
 *  - `presignRicevutaInputSchema` → POST /api/v1/operator/ricevuta/presign
 *  - `commitRicevutaInputSchema`  → POST /api/v1/operator/ricevuta/commit
 *
 * Pattern (INTRODUCTION.md "Pattern 7: Zod Schema with `.strict()` +
 * `superRefine`"):
 *  - `.strict()` sul commit per rifiutare extra-keys (security: blocca
 *    header injection accidentale lato frontend).
 *  - `superRefine` per regole condizionali non esprimibili come campo:
 *      (1) `firmaDigitaleNote` obbligatoria (≥10 char) quando
 *          `firmaDigitaleStatus === 'UNRECOGNIZED'` (BR-4)
 *      (2) `dataRegistrazione` non può essere futura (BR-3)
 *
 * Costanti chiave (vedi spec FEATURE_F-008_*.md):
 *  - protocollo AdE regex: `RLI/MM/YYYY/NNNNNN` — 6 char per progressivo
 *  - sizeBytes max: 10 MiB (10_485_760 byte) — vincolo policy SaaS
 *  - sha256: hex 64-char (lowercase)
 */
import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */
/** Limite size singolo file ricevuta (10 MiB). */
export const RICEVUTA_MAX_SIZE_BYTES = 10_485_760;
/**
 * Pattern protocollo Agenzia delle Entrate: `RLI/MM/YYYY/NNNNNN`.
 *  - `RLI`     — codice prodotto (Registrazione Locazione Immobili)
 *  - `MM`      — mese registrazione (01–12, validato lato AdE)
 *  - `YYYY`    — anno registrazione (4 cifre)
 *  - `NNNNNN`  — progressivo (6 cifre)
 *
 * Esempio valido: `RLI/05/2026/000001`.
 */
export const PROTOCOLLO_ADE_REGEX = /^RLI\/\d{2}\/\d{4}\/\d{6}$/;
/** Pattern sha256 hex lowercase 64-char. */
export const SHA256_HEX_REGEX = /^[0-9a-f]{64}$/;
/* -------------------------------------------------------------------------- */
/* Presign input                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Body request per POST /api/v1/operator/ricevuta/presign.
 *
 * NB: `accountId` e `praticaId` validation (assigned-to-operator,
 * stato ∈ {IN_ELABORAZIONE, IN_CARICO}) sono fatti server-side in
 * `OperatorService.presignRicevutaUpload` (Phase 3) — non esprimibili in
 * Zod (dipendono dallo stato DB).
 */
export const presignRicevutaInputSchema = z.object({
    praticaId: z.string().cuid({ message: 'praticaId deve essere un CUID valido' }),
    contentType: z.literal('application/pdf', {
        errorMap: () => ({ message: 'contentType deve essere application/pdf' }),
    }),
    sizeBytes: z
        .number({ required_error: 'sizeBytes obbligatorio' })
        .int('sizeBytes deve essere un intero')
        .min(1, 'sizeBytes deve essere positivo')
        .max(RICEVUTA_MAX_SIZE_BYTES, `sizeBytes non può superare ${RICEVUTA_MAX_SIZE_BYTES} byte (10 MiB)`),
});
/* -------------------------------------------------------------------------- */
/* Commit input                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Stato firma digitale (manuale, V1 — vedi spec):
 *  - `VALID`        → firma rilevata e valida (operatore ha verificato)
 *  - `UNRECOGNIZED` → firma presente ma non riconosciuta — `firmaDigitaleNote`
 *                     obbligatoria (≥10 char)
 *  - `SKIPPED`      → step verifica saltato (es. ricevuta cartacea scansionata)
 */
export const firmaDigitaleStatusSchema = z.enum([
    'VALID',
    'UNRECOGNIZED',
    'SKIPPED',
]);
/**
 * Body request per POST /api/v1/operator/ricevuta/commit.
 *
 * `.strict()`: rifiuta extra-keys per evitare smuggling di campi non previsti
 * dal frontend (security best-practice — Pattern 7).
 *
 * Validazioni cross-field via `superRefine`:
 *  - BR-4: `firmaDigitaleNote` obbligatoria (≥10 char) quando
 *    `firmaDigitaleStatus === 'UNRECOGNIZED'`
 *  - BR-3: `dataRegistrazione` non può essere futura (confronto stringhe ISO
 *    `YYYY-MM-DD` — funziona perché lo schema garantisce formato `.date()`).
 */
const commitRicevutaInputBaseSchema = z
    .object({
    praticaId: z.string().cuid({ message: 'praticaId deve essere un CUID valido' }),
    s3Key: z.string().min(1, 's3Key obbligatorio'),
    sha256: z
        .string()
        .regex(SHA256_HEX_REGEX, 'sha256 deve essere un hex lowercase di 64 caratteri'),
    sizeBytes: z
        .number()
        .int('sizeBytes deve essere un intero')
        .min(1, 'sizeBytes deve essere positivo')
        .max(RICEVUTA_MAX_SIZE_BYTES, `sizeBytes non può superare ${RICEVUTA_MAX_SIZE_BYTES} byte (10 MiB)`),
    protocolloAde: z
        .string()
        .regex(PROTOCOLLO_ADE_REGEX, 'Formato protocollo non valido (atteso: RLI/MM/YYYY/NNNNNN)'),
    dataRegistrazione: z
        .string()
        .date('Data registrazione non valida (formato atteso: YYYY-MM-DD)'),
    firmaDigitaleStatus: firmaDigitaleStatusSchema,
    firmaDigitaleNote: z.string().optional(),
    /**
     * Opzionale: chiave S3 dell'XML AdE collegato (nessuna logica di storage
     * lato backend in F-008 — solo persistenza del riferimento).
     */
    xmlS3Key: z.string().optional(),
})
    .strict();
export const commitRicevutaInputSchema = commitRicevutaInputBaseSchema.superRefine((data, ctx) => {
    // BR-4: motivazione richiesta quando firma non riconosciuta
    if (data.firmaDigitaleStatus === 'UNRECOGNIZED') {
        const note = data.firmaDigitaleNote?.trim() ?? '';
        if (note.length < 10) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['firmaDigitaleNote'],
                message: 'Motivazione obbligatoria (min 10 caratteri) quando la firma digitale non è riconosciuta',
            });
        }
    }
    // BR-3: data registrazione non può essere futura
    // Confronto stringhe ISO `YYYY-MM-DD`: lessicograficamente equivalente
    // al confronto cronologico (entrambi normalizzati dallo schema `.date()`).
    const today = new Date().toISOString().slice(0, 10);
    if (data.dataRegistrazione > today) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dataRegistrazione'],
            message: 'La data di registrazione non può essere futura',
        });
    }
});
//# sourceMappingURL=ricevuta-input.js.map